import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatTL } from "@/lib/api";
import { Package, User, Wallet, ArrowUpRight, ArrowDownRight, Eye } from "lucide-react";
import OrderDetailModal from "@/components/OrderDetailModal";

export default function Account() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [cari, setCari] = useState({ transactions: [], current_balance: 0, credit_limit: 0 });
  const [tab, setTab] = useState("orders");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/my").then(r => setOrders(r.data));
    if (user.role === "b2b_dealer") {
      api.get("/cari/transactions").then(r => setCari(r.data)).catch(()=>{});
    }
  }, [user]);

  if (loading) return <div className="kz-container py-20 text-center text-ink-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace/>;

  const isB2B = user.role === "b2b_dealer";

  return (
    <div className="kz-container py-10">
      <h1 className="font-display text-3xl md:text-4xl font-black mb-2">Hesabım</h1>
      <p className="text-ink-500 mb-8">Hoş geldiniz, {user.full_name}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="kz-card p-6">
          <User className="text-brand mb-2"/>
          <div className="text-xs uppercase tracking-wider text-ink-500">Hesap</div>
          <div className="font-bold">{user.email}</div>
          <div className="text-sm text-ink-500 mt-1">{user.phone || "Telefon eklenmemiş"}</div>
        </div>
        {user.b2b_status && user.b2b_status !== "none" && (
          <div className="kz-card p-6 border-b2b">
            <div className="text-xs uppercase tracking-wider text-b2b font-bold">Bayi Durumu</div>
            <div className="font-display font-black text-xl mt-1">
              {user.b2b_status === "approved" ? "✓ Onaylı" : user.b2b_status === "pending" ? "⏳ Onay Bekleniyor" : "Reddedildi"}
            </div>
            {isB2B && (
              <Link to="/bayi-paneli" className="text-sm text-b2b font-semibold hover:underline mt-2 inline-block">Bayi Paneline Git →</Link>
            )}
          </div>
        )}
        <div className="kz-card p-6">
          <Package className="text-brand mb-2"/>
          <div className="text-xs uppercase tracking-wider text-ink-500">Toplam Sipariş</div>
          <div className="font-display font-black text-2xl">{orders.length}</div>
        </div>
      </div>

      {isB2B && (
        <div className="flex gap-1 mb-4 border-b border-ink-200">
          <button data-testid="tab-orders" onClick={()=>setTab("orders")} className={`px-4 py-3 font-semibold text-sm border-b-2 ${tab==="orders" ? "border-ink-900 text-ink-900" : "border-transparent text-ink-500"}`}>Siparişlerim</button>
          <button data-testid="tab-cari" onClick={()=>setTab("cari")} className={`px-4 py-3 font-semibold text-sm border-b-2 ${tab==="cari" ? "border-ink-900 text-ink-900" : "border-transparent text-ink-500"}`}>Cari Hesap Ekstresi</button>
        </div>
      )}

      {(tab === "orders" || !isB2B) && (
        <div className="kz-card divide-y divide-ink-200">
          {orders.length === 0 ? (
            <div className="p-10 text-center text-ink-500">Henüz siparişiniz yok.</div>
          ) : orders.map(o => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className="p-4 flex items-center justify-between gap-4 flex-wrap hover:bg-ink-50">
              <div>
                <div className="font-mono text-xs text-ink-500">{o.order_number}</div>
                <div className="font-bold">{o.items.length} ürün</div>
                <div className="text-xs text-ink-500">{new Date(o.created_at).toLocaleString("tr-TR")}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="font-display font-black text-lg">{formatTL(o.total)}</div>
                  <div className="flex gap-2 mt-1 justify-end">
                    <span className={`text-[10px] px-2 py-0.5 font-bold ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {o.payment_status === "paid" ? "ÖDENDİ" : "BEKLİYOR"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 font-bold bg-ink-100">{o.order_status.toUpperCase()}</span>
                  </div>
                </div>
                <button data-testid={`view-${o.order_number}`} onClick={()=>setSelectedOrder(o)} className="kz-btn-outline text-xs py-2 px-3"><Eye size={14}/> Detay</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isB2B && tab === "cari" && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="kz-card p-5 border-l-4 border-b2b">
              <Wallet className="text-b2b mb-2"/>
              <div className="text-xs uppercase text-ink-500">Mevcut Bakiye</div>
              <div className={`font-display font-black text-2xl ${cari.current_balance < 0 ? "text-red-600" : "text-green-600"}`}>{formatTL(cari.current_balance)}</div>
              <div className="text-xs text-ink-500">{cari.current_balance >= 0 ? "Alacaklı" : "Borçlu"}</div>
            </div>
            <div className="kz-card p-5">
              <div className="text-xs uppercase text-ink-500 mb-2">Kredi Limiti</div>
              <div className="font-display font-black text-2xl">{formatTL(cari.credit_limit)}</div>
            </div>
            <div className="kz-card p-5">
              <div className="text-xs uppercase text-ink-500 mb-2">Toplam Hareket</div>
              <div className="font-display font-black text-2xl">{cari.transactions.length}</div>
            </div>
          </div>

          <div className="kz-card overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-100">
                <tr className="text-left text-xs uppercase tracking-wider">
                  <th className="p-3">Tarih</th><th className="p-3">Açıklama</th><th className="p-3 text-right">Tutar</th><th className="p-3 text-right">Bakiye</th>
                </tr>
              </thead>
              <tbody>
                {cari.transactions.length === 0 && <tr><td colSpan="4" className="p-10 text-center text-ink-500">Henüz cari hareket yok</td></tr>}
                {cari.transactions.map((t, i) => (
                  <tr key={t.id} className={i % 2 ? "bg-ink-100/50" : ""}>
                    <td className="p-3 text-xs font-mono">{new Date(t.created_at).toLocaleString("tr-TR")}</td>
                    <td className="p-3 text-sm">
                      {t.description}
                      {t.related_order && <span className="ml-2 font-mono text-xs text-ink-500">({t.related_order})</span>}
                    </td>
                    <td className={`p-3 text-right font-bold whitespace-nowrap ${t.amount < 0 ? "text-red-600" : "text-green-600"}`}>
                      {t.amount < 0 ? <ArrowDownRight size={12} className="inline"/> : <ArrowUpRight size={12} className="inline"/>} {formatTL(Math.abs(t.amount))}
                    </td>
                    <td className="p-3 text-right font-mono text-sm">{formatTL(t.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={()=>setSelectedOrder(null)}/>}
    </div>
  );
}
